import React from "react";
import { useNavigate } from "react-router-dom";
import "./TenthTwelfthStudents.css";

export default function TenthTwelfthStudents() {
  const navigate = useNavigate();

  const schoolRoadmap = [
    {
      topic: "Linux",
      percentage: 5,
      startDay: 1,
      endDay: 18,
      totalDays: 18,
      focus:
        "Terminal basics, file system, commands, permissions, process basics, and developer workflow.",
      checkpoints: [
        "Linux command assignment",
        "Terminal practice task",
        "Basic environment setup review",
      ],
    },
    {
      topic: "HTML",
      percentage: 6,
      startDay: 19,
      endDay: 54,
      totalDays: 36,
      focus:
        "HTML structure, semantic tags, forms, tables, media, SEO basics, and page structure.",
      checkpoints: ["HTML page assignment", "Resume webpage mini project", "Structure review"],
    },
    {
      topic: "CSS",
      percentage: 6,
      startDay: 55,
      endDay: 90,
      totalDays: 36,
      focus:
        "Selectors, box model, flexbox, grid, responsive design, animations, and modern UI styling.",
      checkpoints: ["Landing page assignment", "Responsive layout project", "UI review session"],
    },
    {
      topic: "Python",
      percentage: 11,
      startDay: 91,
      endDay: 171,
      totalDays: 81,
      focus:
        "Python fundamentals, functions, OOP, files, error handling, modules, scripting, and problem solving.",
      checkpoints: ["Python practice assignments", "Automation mini project", "Coding mock discussion"],
    },
    {
      topic: "FastAPI",
      percentage: 5,
      startDay: 172,
      endDay: 207,
      totalDays: 36,
      focus:
        "API creation, routing, request/response models, validation, authentication basics, and backend structure.",
      checkpoints: ["FastAPI assignment", "CRUD API mini project", "API testing review"],
    },
    {
      topic: "JavaScript",
      percentage: 5,
      startDay: 208,
      endDay: 243,
      totalDays: 36,
      focus:
        "Variables, functions, DOM, events, async JavaScript, fetch API, and browser logic.",
      checkpoints: ["JavaScript assignment", "DOM mini project", "Frontend logic review"],
    },
    {
      topic: "React.js",
      percentage: 9,
      startDay: 244,
      endDay: 303,
      totalDays: 60,
      focus:
        "Components, props, state, hooks, routing, API integration, forms, and frontend project building.",
      checkpoints: ["React assignment", "Frontend project", "Mock project explanation"],
    },
    {
      topic: "MongoDB",
      percentage: 5,
      startDay: 304,
      endDay: 324,
      totalDays: 21,
      focus:
        "Collections, documents, CRUD, schema design basics, aggregation basics, and backend integration.",
      checkpoints: ["MongoDB CRUD assignment", "Database integration task", "Data model review"],
    },
    {
      topic: "Docker",
      percentage: 6,
      startDay: 325,
      endDay: 366,
      totalDays: 42,
      focus:
        "Containers, images, Dockerfile, volumes, networks, compose basics, and deployment mindset.",
      checkpoints: ["Docker assignment", "Containerize project", "Deployment review"],
    },
    {
      topic: "Kubernetes",
      percentage: 6,
      startDay: 367,
      endDay: 408,
      totalDays: 42,
      focus:
        "Pods, deployments, services, scaling basics, config, and production system understanding.",
      checkpoints: ["Kubernetes basics task", "Deployment YAML practice", "Infra mock discussion"],
    },
    {
      topic: "GitHub",
      percentage: 5,
      startDay: 409,
      endDay: 429,
      totalDays: 21,
      focus:
        "Repositories, pull requests, README writing, project hosting, and portfolio presentation.",
      checkpoints: ["GitHub portfolio setup", "README review", "Profile improvement"],
    },
    {
      topic: "Artificial Intelligence",
      percentage: 16,
      startDay: 430,
      endDay: 540,
      totalDays: 111,
      focus:
        "AI fundamentals, machine learning, deep learning, GenAI, LLMs, NLP, computer vision, model usage, and AI project building.",
      checkpoints: [
        "AI assignment",
        "ML/AI mini project",
        "GenAI project",
        "AI project presentation",
        "Technical mock interview",
      ],
    }
  ];

  const dailyRoutine = [
    { time: "7:00 AM – 7:30 AM", task: "Logic / Coding Warm-up", icon: "💻", type: "Preparation" },
    { time: "7:30 AM – 8:00 AM", task: "Breakfast + School Preparation", icon: "🌅", type: "Life Balance" },
    { time: "8:00 AM – 4:00 PM", task: "School / Junior College / Board Study", icon: "🏫", type: "School Time" },
    { time: "4:00 PM – 5:00 PM", task: "Rest / Snacks / Refresh", icon: "☕", type: "Life Balance" },
    { time: "5:00 PM – 6:30 PM", task: "Board Exam / Entrance Foundation Study", icon: "📚", type: "School Time" },
    { time: "6:30 PM – 7:30 PM", task: "Assignment Work / Guided Learning", icon: "📌", type: "Preparation" },
    { time: "7:30 PM – 8:30 PM", task: "Dinner + Family Time", icon: "🍱", type: "Life Balance" },
    { time: "8:30 PM – 9:15 PM", task: "Project Building / Coding Practice", icon: "🚀", type: "Preparation" },
    { time: "9:15 PM – 9:45 PM", task: "Doubt Solving / Peer Discussion / Revision", icon: "🔁", type: "Preparation" },
    { time: "9:45 PM – 10:00 PM", task: "Next-Day Planning + Sleep Preparation", icon: "🌙", type: "Life Balance" },
  ];

  const everyTopicIncludes = [
    "Guided lecture learning and study flow",
    "Notes building and daily revision",
    "Assignments after topic completion",
    "Doubt solving and discussion rounds",
    "Mini projects and implementation tasks",
    "Weekend review and parent progress tracking",
    "Mock explanation or presentation practice",
    "GitHub / portfolio update wherever applicable",
  ];

  const preparationTasks = [
    {
      icon: "🎓",
      name: "Guided Lecture Session",
      text: "Structured lecture study, mentor-guided topic flow, and concept understanding.",
    },
    {
      icon: "📝",
      name: "Notes Building",
      text: "Writing important points, examples, commands, syntax, mistakes, and interview notes.",
    },
    {
      icon: "💻",
      name: "Coding Practice",
      text: "Hands-on coding, logic building, examples, implementation, and debugging practice.",
    },
    {
      icon: "📌",
      name: "Assignment Work",
      text: "Completing assigned tasks, topic exercises, school-friendly projects, and improvement work.",
    },
    {
      icon: "🚀",
      name: "Project Building",
      text: "Building portfolio projects, GitHub updates, demos, and project explanation confidence.",
    },
    {
      icon: "❓",
      name: "Doubt Solving",
      text: "Clearing pending doubts with mentor or team support during available guidance windows.",
    },
    {
      icon: "🤝",
      name: "Peer Discussion",
      text: "Discussion with other learners, explanation practice, group learning, and confidence building.",
    },
    {
      icon: "🎤",
      name: "Presentation Practice",
      text: "Viva, science fair, project demo, PPT explanation, and public speaking practice.",
    },
  ];

  const lifeBalanceTasks = [
    {
      icon: "🏫",
      name: "School / Junior College Time",
      text: "School lectures, practicals, board preparation, assignments, and exams are protected.",
    },
    {
      icon: "📚",
      name: "Board + Entrance Balance",
      text: "Separate time for board exam study, CET/JEE/NEET foundation, or stream-specific academic goals.",
    },
    {
      icon: "🌅",
      name: "Morning Discipline",
      text: "Breakfast, school preparation, planning, and routine setup before school hours.",
    },
    {
      icon: "🌙",
      name: "Sleep Discipline",
      text: "Healthy sleep planning is protected because school students need recovery and consistency.",
    },
  ];

  const schoolAdvantage = [
    "Research paper foundation and technical writing guidance",
    "Science fair, innovation project, and model presentation support",
    "Junior hackathon and coding competition preparation",
    "Olympiad / logic / aptitude foundation support where relevant",
    "Conference awareness and student presentation guidance",
    "Public speaking, PPT, viva, and project demo practice",
    "Parent progress review and career counselling support",
    "Early GitHub, LinkedIn, resume, and portfolio foundation",
  ];

  const jobSprint = [
    {
      title: "Career Direction & Stream Clarity",
      image: "/placement/positioning.png",
      items: [
        "Understand AI, software, data, full stack, and research career paths",
        "Connect school subjects with future technology careers",
        "Plan long-term direction for college, internships, and projects",
        "Build clarity with student and parent counselling",
      ],
    },
    {
      title: "Portfolio Foundation",
      image: "/placement/resume-linkedin.png",
      items: [
        "Create early GitHub profile",
        "Build clean project repositories",
        "Prepare student-level resume foundation",
        "Start LinkedIn and technical identity step-by-step",
      ],
    },
    {
      title: "Project Explanation Confidence",
      image: "/placement/project-demo.png",
      items: [
        "Practice explaining school-level and AI projects clearly",
        "Prepare architecture and workflow explanation",
        "Build answers for challenges, bugs, and improvements",
        "Create demo-ready presentation flow",
      ],
    },
    {
      title: "Research & Innovation Preparation",
      image: "/placement/technical-interview.png",
      items: [
        "Learn how to select research-style topics",
        "Prepare abstract, report, and methodology basics",
        "Work on science fair or innovation ideas",
        "Practice paper or project presentation",
      ],
    },
    {
      title: "Communication & Confidence",
      image: "/placement/hr-communication.png",
      items: [
        "Prepare strong self-introduction",
        "Practice English and technical explanation",
        "Handle viva, seminar, and Q&A confidence",
        "Improve presentation and communication maturity",
      ],
    },
    {
      title: "College Admission Advantage",
      image: "/placement/job-application.png",
      items: [
        "Use projects to strengthen future college profile",
        "Build proof for internships and competitions",
        "Prepare for technical clubs and student communities",
        "Plan career roadmap before college starts",
      ],
    },
    {
      title: "Mock Panels & Reviews",
      image: "/placement/mock-interview.png",
      items: [
        "Regular project explanation practice",
        "Panel discussion and presentation rounds",
        "Portfolio walkthrough practice",
        "Feedback-based improvement plan after each session",
      ],
    },
    {
      title: "Long-Term Career Foundation",
      image: "/placement/final-push.png",
      items: [
        "Create a serious learning identity early",
        "Build confidence before college competition starts",
        "Plan future internships and advanced projects",
        "Continue support toward strong career readiness",
      ],
    },
  ];

  return (
    <main className="B-school-page">
      {/* HERO */}
      <section className="B-school-hero">
        <div className="B-school-hero-content">
          <p className="section-kicker">10TH & 12TH STUDENTS ROADMAP</p>

          <h1>An up to 18-month future-ready roadmap for school and junior college students.</h1>

          <p>
            Build AI, Full Stack, coding, research mindset, project confidence,
            presentation skills, portfolio foundation, and long-term career direction
            while balancing school, board exams, and entrance preparation.
          </p>

          <div className="B-salary-band">
            <p className="section-kicker B-salary-kicker">Target preparation range</p>
            <strong>Up to ₹50+ LPA</strong>
            <small>
              Based on long-term discipline, skills, project quality, college path,
              internship exposure, interview performance, market conditions, and career
              readiness over time.
            </small>
          </div>

          <div className="B-school-hero-actions">
            <button onClick={() => navigate("/counselling?roadmap=10th-12th")}>
              Apply for Counselling
            </button>
            <a href="#school-roadmap">View 18-Month Roadmap</a>
          </div>
        </div>
      </section>

      {/* PRINCIPLE */}
      <section className="B-school-section">
        <div className="B-school-principle-box">
          <div>
            <p className="section-kicker">SCHOOL + FUTURE CAREER PRINCIPLE</p>

            <h2>
              School students need direction early. We convert early years into career
              advantage.
            </h2>

            <p>
              The goal is not to ignore school. The goal is to balance school, board
              exams, entrance preparation, coding, AI, projects, communication, research
              mindset, and long-term career planning with proper guidance.
            </p>
          </div>

          <div className="B-mentor-clarity-card">
            <h3>Guided, not pressured.</h3>

            <p>
              10th and 12th students need clarity, not confusion. B. Akshay personally
              guides students and parents on career direction, AI industry clarity,
              study discipline, project direction, and important high-level doubts.
            </p>

            <p>
              Along with mentor guidance, the StudyAIPlacement team supports students
              through assignments, projects, presentations, paper-writing foundation,
              competition preparation, portfolio building, and confidence development.
            </p>

            <p>
              The goal is to help serious school students become future-ready before
              college competition starts.
            </p>
          </div>
        </div>
      </section>

      {/* DAILY TASK SYSTEM */}
      <section className="B-school-section">
        <div className="B-daily-task-system">
          <div className="B-school-heading B-compact">
            <p className="section-kicker">DAILY TASK SYSTEM</p>

            <h2>School, boards, coding, and projects need a balanced system.</h2>

            <p>
              Students follow a protected daily structure. School and board preparation
              remain important, while morning and evening blocks build coding, AI,
              assignments, projects, doubts, peer discussion, and communication confidence.
            </p>
          </div>

          <div className="B-task-system-columns">
            <div className="B-task-system-panel">
              <h3>Preparation related tasks</h3>

              <div className="B-task-chip-grid">
                {preparationTasks.map((task) => (
                  <div className="B-task-chip-card" key={task.name}>
                    <div className="B-task-icon">{task.icon}</div>
                    <span>{task.name}</span>
                    <p>{task.text}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="B-task-system-panel B-life">
              <h3>School and life balance</h3>

              <div className="B-task-chip-grid">
                {lifeBalanceTasks.map((task) => (
                  <div className="B-task-chip-card" key={task.name}>
                    <div className="B-task-icon">{task.icon}</div>
                    <span>{task.name}</span>
                    <p>{task.text}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* DAILY ROUTINE */}
      <section className="B-school-section">
        <div className="B-school-heading">
          <p className="section-kicker">SCHOOL + FUTURE SKILL ROUTINE</p>

          <h2>A practical routine for students balancing school and future skills.</h2>

          <p>
            The timetable protects school and board preparation while using morning and
            evening blocks for coding, AI, projects, revision, presentation practice,
            and guided learning.
          </p>
        </div>

        <div className="B-routine-grid">
          {dailyRoutine.map((item) => (
            <div
              className={`B-routine-card ${
                item.type === "School Time"
                  ? "B-school-routine-card"
                  : item.type === "Life Balance"
                    ? "B-life-routine-card"
                    : "B-prep-routine-card"
              }`}
              key={item.time}
            >
              <div className="B-routine-card-top">
                <span>{item.time}</span>
                <small>{item.type}</small>
              </div>

              <div className="B-routine-task-line">
                <b>{item.icon}</b>
                <p>{item.task}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="B-weekend-system">
          <div>
            <strong>Saturday review + parent update</strong>
            <p>
              Weekly revision, assignment review, project checkpoint, presentation
              practice, parent progress update, and improvement planning.
            </p>
          </div>

          <div>
            <strong>Sunday recovery + school balance</strong>
            <p>
              Recovery, family time, school homework, board study, and optional light
              revision so the student stays consistent without burnout.
            </p>
          </div>
        </div>
      </section>

      {/* ROADMAP */}
      <section className="B-school-section" id="school-roadmap">
        <div className="B-school-heading">
          <p className="section-kicker">UP TO 18-MONTH SKILL ROADMAP</p>

          <h2>Every phase builds long-term career advantage before college.</h2>

          <p>
            This 18-month roadmap gives school and junior college students enough time to
            build fundamentals, frontend, backend, databases, DevOps, mobile development,
            AI, Data Science, GenAI, projects, portfolio foundation, research mindset,
            and communication confidence.
          </p>
        </div>

        <div className="B-roadmap-unlock-list">
          {schoolRoadmap.map((item, index) => (
            <div className="B-unlock-card" key={item.topic}>
              <div className="B-unlock-top">
                <span>Phase {String(index + 1).padStart(2, "0")}</span>
                <b>{item.percentage}%</b>
              </div>

              <h3>{item.topic}</h3>

              <div className="B-day-pill">
                Day {item.startDay} – Day {item.endDay}
                <small>{item.totalDays} days</small>
              </div>

              <p>{item.focus}</p>

              <div className="B-checkpoint-list">
                {item.checkpoints.map((checkpoint) => (
                  <span key={checkpoint}>{checkpoint}</span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* EVERY TOPIC */}
      <section className="B-school-section">
        <div className="B-topic-system-box">
          <div className="B-school-heading B-compact">
            <p className="section-kicker">INSIDE EVERY TOPIC</p>

            <h2>Students do not just watch lectures. They build, explain, and improve.</h2>
          </div>

          <div className="B-topic-system-grid">
            {everyTopicIncludes.map((item, index) => (
              <div className="B-topic-system-card" key={item}>
                <span>{String(index + 1).padStart(2, "0")}</span>
                <p>{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SCHOOL ADVANTAGE */}
      <section className="B-school-section">
        <div className="B-topic-system-box">
          <div className="B-school-heading B-compact">
            <p className="section-kicker">SCHOOL ADVANTAGE TRACK</p>

            <h2>Students can build rare proof before college even begins.</h2>

            <p>
              School years can become a powerful foundation for research, innovation,
              competitions, science fairs, communication, portfolio building, and future
              college advantage.
            </p>
          </div>

          <div className="B-topic-system-grid">
            {schoolAdvantage.map((item, index) => (
              <div className="B-topic-system-card" key={item}>
                <span>{String(index + 1).padStart(2, "0")}</span>
                <p>{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FUTURE PREP */}
      <section className="B-school-section B-placement-journey-section">
        <div className="B-school-heading">
          <p className="section-kicker">AFTER THE 18-MONTH ROADMAP</p>

          <h2>
            From school student to future-ready learner, students build serious early
            career direction.
          </h2>

          <p>
            Support is not limited to coding. Students move through career direction,
            portfolio foundation, project explanation, research and innovation exposure,
            communication training, college admission advantage, mock panels, and
            long-term career planning.
          </p>
        </div>

        <div className="B-placement-journey">
          {jobSprint.map((phase, index) => (
            <div className="B-placement-step" key={phase.title}>
              <div className="B-placement-step-number">
                {String(index + 1).padStart(2, "0")}
              </div>

              <div className="B-placement-step-content">
                <div className="B-placement-visual">
                  <img src={phase.image} alt={phase.title} />
                </div>

                <div className="B-placement-text">
                  <span>Future Step {index + 1}</span>
                  <h3>{phase.title}</h3>

                  <ul>
                    {phase.items.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* OUTCOME */}
      <section className="B-school-section">
        <div className="B-outcome-box">
          <p className="section-kicker">WHAT STUDENTS BUILD</p>

          <h2>Students finish with early proof of skill and future direction.</h2>

          <p>
            By the end of the roadmap, serious students are expected to build visible
            proof that supports future college, internships, competitions, projects,
            communication, and long-term AI/software career preparation.
          </p>

          <div className="B-outcome-grid">
            <span>AI + Full Stack project foundation</span>
            <span>GitHub profile with clean repositories</span>
            <span>Student resume and portfolio foundation</span>
            <span>Deployed beginner-to-advanced projects</span>
            <span>Backend APIs with database integration</span>
            <span>AI / ML / GenAI project demos</span>
            <span>Research paper foundation</span>
            <span>Science fair and innovation project readiness</span>
            <span>Hackathon and competition participation proof</span>
            <span>Presentation and communication confidence</span>
            <span>College admission advantage</span>
            <span>Long-term career direction</span>
          </div>

          <button onClick={() => navigate("/counselling?roadmap=10th-12th")}>
            Apply for Counselling
          </button>
        </div>
      </section>
    </main>
  );
}